import http from '../utils/http';
import {
  LoginRequestModel,
  QrCodeVerifyIdentityModel
} from '../models/requests/auth.model';
import { ApiResponse } from '../models';
import { getCookie } from '../utils/helpers';
import { filterEnabledBackends } from '../utils/backend-availability';

interface BackendConfig {
  name: string;
  url: string;
  username: string;
  password: string;
}

interface BackendAuthToken {
  name: string;
  url: string;
  token: string;
}

const backendTokensStorageKey = 'admin-backend-auth-tokens';

const primaryAuthTokenStorageKey = 'primary-admin-auth-token';

const primaryAuthBackendStorageKey = 'primary-admin-auth-backend';

function getBackends(): BackendConfig[] {
  const raw = process.env.REACT_APP_BACKENDS;


  if (!raw) {
    return [];
  }

  try {
    let value = raw.trim();

    if (
      value.startsWith("'") &&
      value.endsWith("'")
    ) {
      value = value.slice(1, -1);
    }

    const parsed = JSON.parse(value);



    return Array.isArray(parsed)
      ? filterEnabledBackends(parsed)
      : [];
  } catch (error) {
    console.error(
      '[AUTH] Failed to parse REACT_APP_BACKENDS:',
      error
    );

    return [];
  }
}

const backends: BackendConfig[] =
  getBackends();



function getAuthTokenFromResponse(response: any): string | null {
  return response?.data?.token || response?.token || null;
}


function getBackendAuthTokens(): BackendAuthToken[] {
  try {
    const stored = localStorage.getItem(
      backendTokensStorageKey
    );

    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);

    return Array.isArray(parsed)
      ? filterEnabledBackends(parsed)
      : [];
  } catch {
    return [];
  }
}


function setBackendAuthTokens(
  tokens: BackendAuthToken[]
): void {
  localStorage.setItem(
    backendTokensStorageKey,
    JSON.stringify(tokens)
  );
}


function clearBackendAuthTokens(): void {
  localStorage.removeItem(
    backendTokensStorageKey
  );

  localStorage.removeItem(
    primaryAuthTokenStorageKey
  );

  localStorage.removeItem(
    primaryAuthBackendStorageKey
  );
}


function getPrimaryAdminAuthToken(): string | null {
  return (
    localStorage.getItem(primaryAuthTokenStorageKey) ||
    getCookie('Auth-Token')
  );
}
function getPrimaryAdminBackend(): BackendAuthToken | null {
  try {
    const stored = localStorage.getItem(
      primaryAuthBackendStorageKey
    );

    if (!stored) {
      return null;
    }

    return JSON.parse(stored);
  } catch {
    return null;
  }
}

function setPrimaryAdminAuthToken(
  token?: string | null
): void {
  if (token) {
    localStorage.setItem(
      primaryAuthTokenStorageKey,
      token
    );
  } else {
    localStorage.removeItem(
      primaryAuthTokenStorageKey
    );
  }
}
function setPrimaryAdminBackend(
  backend: BackendAuthToken | null
): void {
  if (backend) {
    localStorage.setItem(
      primaryAuthBackendStorageKey,
      JSON.stringify(backend)
    );
  } else {
    localStorage.removeItem(
      primaryAuthBackendStorageKey
    );
  }
}


async function login(data: LoginRequestModel) {
  clearBackendAuthTokens();

  if (!backends.length) {
    console.error('[AUTH] No backends configured');

    throw new Error(
      'No backend servers are configured'
    );
  }

  const successfulBackends: BackendAuthToken[] = [];

  let primaryBackendFromUserCredentials: BackendAuthToken | null = null;

  let firstSuccessfulResponse: any = null;

  /*
   * ============================================================
   * FAZA 1
   *
   * Na SVIM backendima prvo pokušavamo username/password
   * koje je korisnik unio na login formi.
   * ============================================================
   */

  const userCredentialResults = await Promise.all(
    backends.map(async (backend) => {
      const loginUrl =
        `${backend.url.replace(/\/$/, '')}/api/users/challenge/login`;

      try {
        const response = await http.post(
          loginUrl,
          data,
          {
            withCredentials: true,
            headers: {
              errorAlert: false,
            },
          }
        );

        const token =
          getAuthTokenFromResponse(response);

        if (token) {
          console.log(
            `[AUTH] ${backend.name}: USER credentials SUCCESS`
          );

          return {
            backend,
            token,
            response,
            userCredentialsSuccess: true,
          };
        }

        console.log(
          `[AUTH] ${backend.name}: USER credentials FAILED`
        );

        return {
          backend,
          token: null,
          response: null,
          userCredentialsSuccess: false,
        };
      } catch (error) {
        console.log(
          `[AUTH] ${backend.name}: USER credentials FAILED`
        );

        return {
          backend,
          token: null,
          response: null,
          userCredentialsSuccess: false,
        };
      }
    })
  );

  /*
   * ============================================================
   * PROVJERA
   *
   * User MORA proći barem na jednom backendu sa svojim
   * username/password.
   *
   * Ako nije prošao ni na jednom -> POGREŠNA ŠIFRA/PASSWORD.
   * .env credentials se u ovom slučaju NE KORISTE.
   * ============================================================
   */

  const userSuccessfulResults =
    userCredentialResults.filter(
      (result) =>
        result.userCredentialsSuccess &&
        result.token
    );

  if (!userSuccessfulResults.length) {
    console.error(
      '[AUTH] USER credentials failed on ALL backends'
    );

    throw new Error(
      'Pogrešno korisničko ime ili lozinka'
    );
  }

  /*
   * ============================================================
   * User je uspješno prijavljen barem na jednom backendu.
   *
   * Ti backendovi odmah ulaze u successfulBackends.
   * ============================================================
   */

  for (const result of userSuccessfulResults) {
    const backendAuth: BackendAuthToken = {
      name: result.backend.name,
      url: result.backend.url,
      token: result.token!,
    };

    successfulBackends.push(
      backendAuth
    );

    /*
     * PRIMARY mora biti backend na kojem su prošli
     * USER credentials, a ne .env credentials.
     */
    if (!primaryBackendFromUserCredentials) {
      primaryBackendFromUserCredentials =
        backendAuth;
    }

    if (!firstSuccessfulResponse) {
      firstSuccessfulResponse =
        result.response;
    }
  }

  /*
   * ============================================================
   * FAZA 2
   *
   * Samo backendima na kojima USER credentials nisu prošli
   * sada pokušavamo credentials iz .env.
   *
   * VAŽNO:
   * Ako je user prošao na PET-u, PET se više ne pokušava
   * sa .env credentials.
   * ============================================================
   */

  const failedUserCredentialResults =
    userCredentialResults.filter(
      (result) =>
        !result.userCredentialsSuccess
    );

  const envCredentialResults =
    await Promise.all(
      failedUserCredentialResults.map(
        async (result) => {
          const backend =
            result.backend;

          const loginUrl =
            `${backend.url.replace(/\/$/, '')}/api/users/challenge/login`;

          try {
            const response =
              await http.post(
                loginUrl,
                {
                  usernameOrEmail:
                    backend.username,
                  password:
                    backend.password,
                },
                {
                  withCredentials: true,
                  headers: {
                    errorAlert: false,
                  },
                }
              );

            const token =
              getAuthTokenFromResponse(
                response
              );

            if (token) {
              console.log(
                `[AUTH] ${backend.name}: DEFAULT .env credentials SUCCESS`
              );

              return {
                backend,
                token,
                response,
              };
            }

            console.log(
              `[AUTH] ${backend.name}: DEFAULT .env credentials FAILED`
            );

            return {
              backend,
              token: null,
              response: null,
            };
          } catch (error) {
            console.log(
              `[AUTH] ${backend.name}: DEFAULT .env credentials FAILED`
            );

            return {
              backend,
              token: null,
              response: null,
            };
          }
        }
      )
    );

  /*
   * ============================================================
   * Dodajemo backendove koji su prošli preko .env credentials.
   * ============================================================
   */

  for (const result of envCredentialResults) {
    if (!result.token) {
      continue;
    }

    successfulBackends.push({
      name: result.backend.name,
      url: result.backend.url,
      token: result.token,
    });

    if (!firstSuccessfulResponse) {
      firstSuccessfulResponse =
        result.response;
    }
  }

  /*
   * ============================================================
   * Mora postojati barem jedan uspješan backend.
   * Ovo bi praktično već bilo zadovoljeno u FAZI 1.
   * ============================================================
   */

  if (!successfulBackends.length) {
    throw new Error(
      'Pogrešno korisničko ime ili lozinka'
    );
  }

  /*
   * Sačuvaj tokene svih uspješnih backenda.
   */
  setBackendAuthTokens(
    successfulBackends
  );

  /*
   * Primary backend je UVIJEK backend gdje je
   * USER username/password uspješno prošao.
   */
  const primaryBackend =
    primaryBackendFromUserCredentials ||
    successfulBackends[0];

  setPrimaryAdminAuthToken(
    primaryBackend.token
  );

  setPrimaryAdminBackend(
    primaryBackend
  );

  console.log(
    '[AUTH] Successful backends:',
    successfulBackends.map(
      (backend) => backend.name
    )
  );

  console.log(
    '[AUTH] Primary backend:',
    primaryBackend.name
  );

  return (
    firstSuccessfulResponse?.data ||
    firstSuccessfulResponse
  );
}


async function checkIfAuthenticated() {
  const tokens = getBackendAuthTokens();

  if (!tokens.length) {
    return null;
  }

  /*
   * Koristi backend koji je prvi uspješno prihvatio
   * USER username/password.
   */
  const storedPrimary =
    getPrimaryAdminBackend();

  const backend =
    tokens.find(
      (token) =>
        token.name === storedPrimary?.name,
    ) ||
    tokens[0];

  const loggedUrl =
    `${backend.url.replace(/\/$/, '')}/api/users/session`;

  try {
    const response = await http.get(
      loggedUrl,
      {
        withCredentials: true,
        headers: {
          'Auth-Token': backend.token,
        },
      }
    );

    setPrimaryAdminAuthToken(
      getAuthTokenFromResponse(response) ||
      backend.token
    );

    setPrimaryAdminBackend(
      backend
    );

    return response.data;
  } catch {
    return null;
  }
}


async function logout() {
  const tokens = getBackendAuthTokens();

  try {
    /*
     * Logout na svim uspješno prijavljenim backendima.
     */
    await Promise.all(
      tokens.map(async (backend) => {
        const logoutUrl =
          `${backend.url.replace(/\/$/, '')}/api/users/challenge/logout`;

        try {
          await http.post(
            logoutUrl,
            {},
            {
              withCredentials: true,
              headers: {
                'Auth-Token': backend.token,
              },
            }
          );
        } catch {
          /*
           * Ako jedan backend ne odgovori,
           * logout ostalih servera i dalje ide dalje.
           */
        }
      })
    );
  } finally {
    clearBackendAuthTokens();
  }
}


async function verifyIdentityWithQrAndPin(
  qrCodeAuthData: QrCodeVerifyIdentityModel
): Promise<any> {
  const primaryBackend = backends[0];

  const response: ApiResponse<any> = await http.post(
    `${primaryBackend.url.replace(/\/$/, '')}/api/users/atm/qrcode/login`,
    qrCodeAuthData
  );

  return response.data;
}


export {
  checkIfAuthenticated,
  login,
  logout,
  verifyIdentityWithQrAndPin,
  getPrimaryAdminAuthToken,
  getBackendAuthTokens,
};
